import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import {
  ShoppingCart, Activity, CalendarDays, Truck, FileInput, Users, Gift,
  Package, Zap, Brain, PackageCheck, BarChart3, Armchair,
  Scissors, Scale, Beef, Ruler, Pill, CakeSlice, Cpu, ChefHat, Settings, Receipt, ChevronRight,
} from 'lucide-react';

const SECTIONS = [
  {
    title: 'Point of Sale',
    accent: 'bg-warning',
    items: [
      { path: '/pos', icon: ShoppingCart, label: 'Quick Entry POS', desc: 'Fast sale entry terminal', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/pos-analytics', icon: Activity, label: 'Live Analytics', desc: 'Real-time sales monitoring', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/daily-summary', icon: CalendarDays, label: 'Daily Summary', desc: 'End-of-day sales summary', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/kitchen', icon: ChefHat, label: 'Kitchen Display', desc: 'Kitchen order management', color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
      { path: '/pos-config', icon: Settings, label: 'POS Config', desc: 'Configure POS settings', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
    ],
  },
  {
    title: 'Sales & Revenue',
    accent: 'bg-success',
    items: [
      { path: '/sales', icon: ShoppingCart, label: 'Sales', desc: 'All sales transactions', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/platforms', icon: Truck, label: 'Online Platforms', desc: 'Delivery platform management', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/invoices', icon: FileInput, label: 'Invoices', desc: 'Customer invoicing', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
      { path: '/order-tracking', icon: Truck, label: 'Order Tracking', desc: 'Track order status', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/customers', icon: Users, label: 'Customers', desc: 'Customer database', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/loyalty', icon: Gift, label: 'Loyalty Program', desc: 'Customer rewards & points', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
    ],
  },
  {
    title: 'Inventory & Stock',
    accent: 'bg-info',
    items: [
      { path: '/stock', icon: Package, label: 'Inventory', desc: 'Stock levels & management', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/stock-reorder', icon: Zap, label: 'AI Reorder', desc: 'Smart reorder suggestions', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/inventory-forecast', icon: Brain, label: 'AI Forecast', desc: 'Demand forecasting', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/transfers', icon: PackageCheck, label: 'Transfers', desc: 'Inter-branch transfers', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/menu-items', icon: Package, label: 'Menu Items', desc: 'Product catalog', color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
      { path: '/addons', icon: Package, label: 'Add-on Library', desc: 'Product add-ons & modifiers', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/menu-analytics', icon: BarChart3, label: 'Menu Analytics', desc: 'Product performance insights', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
    ],
  },
  {
    title: 'Suppliers',
    accent: 'bg-stone-500',
    items: [
      { path: '/suppliers', icon: Truck, label: 'Suppliers', desc: 'Supplier management', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/supplier-payments', icon: Receipt, label: 'Supplier Payments', desc: 'Payment tracking', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/cash-transfers', icon: Receipt, label: 'Cash Transfers', desc: 'Between-account transfers', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
    ],
  },
  {
    title: 'Industry Modules',
    accent: 'bg-orange-500',
    items: [
      { path: '/table-management', icon: Armchair, label: 'Tables', desc: 'Restaurant table management', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/reservations', icon: CalendarDays, label: 'Reservations', desc: 'Booking management', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/salon-appointments', icon: Scissors, label: 'Salon', desc: 'Salon appointments', color: 'text-pink-600', bg: 'bg-pink-50', ring: 'ring-pink-100' },
      { path: '/weight-items', icon: Scale, label: 'Weight Items', desc: 'Weighed product sales', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/meat-cuts', icon: Beef, label: 'Meat Cuts', desc: 'Butcher shop module', color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
      { path: '/tailoring-orders', icon: Ruler, label: 'Tailoring', desc: 'Custom tailoring orders', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/pharmacy-items', icon: Pill, label: 'Pharmacy', desc: 'Pharmacy management', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/bakery-production', icon: CakeSlice, label: 'Bakery', desc: 'Bakery production tracking', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/electronics-warranty', icon: Cpu, label: 'Electronics', desc: 'Electronics & warranty', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/pos-industry-dashboard', icon: Activity, label: 'Industry Dashboard', desc: 'Industry-specific analytics', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
    ],
  },
];

function FeatureCard({ item, navigate }) {
  return (
    <button
      onClick={() => navigate(item.path)}
      className="group flex items-center gap-3.5 p-4 rounded-xl bg-white border border-border hover:border-border hover:shadow-md transition-all duration-200 text-left"
      data-testid={`nav-${item.path.replace('/', '')}`}
    >
      <div className={`w-10 h-10 rounded-xl ${item.bg} ${item.color} flex items-center justify-center shrink-0 ring-1 ${item.ring} group-hover:scale-110 transition-transform duration-200`}>
        <item.icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground text-sm group-hover:text-warning transition-colors">{item.label}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{item.desc}</p>
      </div>
      <ChevronRight size={14} className="text-stone-200 group-hover:text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

export default function POSModulePage() {
  const navigate = useNavigate();
  return (
    <DashboardLayout module="pos">
      <div className="space-y-10 max-w-6xl mx-auto" data-testid="pos-module-page">
        {/* Header */}
        <div className="relative">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">
              <ShoppingCart size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">POS & Sales</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Point of sale, inventory, orders & multi-industry workflows</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.map((section, si) => (
          <div key={section.title} style={{ animationDelay: `${si * 60}ms` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-1 h-5 rounded-full ${section.accent}`} />
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{section.title}</h2>
              <div className="flex-1 h-px bg-muted" />
              <span className="text-[10px] text-muted-foreground font-medium">{section.items.length} items</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item) => (
                <FeatureCard key={item.path} item={item} navigate={navigate} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
